import Vector from "victor";
import Camera from "./Camera";
import CanvasBasedDOMRepresentation from "./CanvasBasedDOMRepresentation";
import { PDFInfo } from "./PDFController";
import { EraserShape2 } from "./pen/PenShape";
import { NodeShape } from "./standardShapes/NodeShape";
import {
  RectRenderer,
  StandardShapeRenderer,
} from "./standardShapes/StandardShapeRenderers";
import { QuillHelper, QuillHelperShapeData } from "./text/QuillHelper";
import { AttributeKey } from "./types/attributes";
import Rect from "./types/Rect";
import { Scope } from "./types/Scope";
import { Shape2 } from "./types/Shape";
import { ShapeDOMRepresentation } from "./types/ShapeDOMRepresentation";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";
import { shapeScale2, shapeTruePosition2 } from "./util";

const DEBUG = false;

export class NodeDOMRepresentation
  extends CanvasBasedDOMRepresentation
  implements ShapeDOMRepresentation
{
  private renderer: StandardShapeRenderer;
  private quillHelper: QuillHelper;
  private textPadding = new Vector(10, 10);

  private baseRootClassName = "WBNodeRoot";

  private image?: HTMLImageElement;
  private pdfInfo?: PDFInfo;
  private loadedPDFURL?: string;
  private pdfBackingCanvas?: HTMLCanvasElement;

  get pageNumberAsNumber(): number | null {
    // band-aid: pageNumber was polluted with strings
    if (!this.nodeShape.pageNumber) return null;
    return parseInt(this.nodeShape.pageNumber as unknown as string, 10);
  }

  constructor(
    private wb: WhiteboardControllerInterface,
    id: string,
    shape: Shape2,
    transformEl: HTMLElement,
    camera: Camera,
    shouldDraw = true
  ) {
    super(id, shape, transformEl, camera);
    this.root.className = this.baseRootClassName;

    if (DEBUG) {
      const labelEl = document.createElement("label");
      labelEl.innerText = id;
      labelEl.style.fontSize = "11px";
      labelEl.style.position = "absolute";
      labelEl.style.left = "100px";
      labelEl.style.top = "100px";
      this.root.appendChild(labelEl);
      // this.root.style.border = "1px solid red";
    }

    this.renderer = new RectRenderer({
      shadowOffsetX: 2,
      shadowOffsetY: 2,
      shadowBlur: 5,
      shadowColor: "rgba(0, 0, 0, 0.24)",
    });

    if (shouldDraw) {
      this.redraw();
    }

    this.quillHelper = new QuillHelper(
      wb,
      this.id,
      this.root,
      this.quillHelperShapeData,
      {
        // this.wb.focusedTextShapesBus.emit(this),
        quillHelperWantsToSetParentElementClassName: (className) =>
          (this.root.className = `${this.baseRootClassName} ${className}`),
        quillHelperWantsToSetParentElementUserSelect: (value) =>
          (this.root.style.userSelect = value),
        quillHelperWantsToDelete: () =>
          this.wb.shapeStore.sendDelete(Scope.Persist, this.id),
        quillHelperWantsToSave: (scope) => this.save(scope),
      }
    );
    this.applyTextElementTransform();
  }

  teardown() {
    super.teardown();
    this.root.parentElement?.removeChild(this.root);
    this.quillHelper.teardown();
  }

  handleImageURLChanged = (imageURL: string | undefined) => {
    if (imageURL && imageURL.match(/\.pdf$/)) {
      this.handlePDFURLChanged(imageURL);
      return;
    }
    if (this.image?.src === imageURL) return;
    if (!this.image) {
      this.image = document.createElement("img");
      this.image.onload = this.handleImageLoaded;
    }
    if (imageURL) {
      this.image.crossOrigin = "anonymous";
      this.image.src = imageURL;
    } else {
      this.handleImageLoaded(); // clear image
    }
  };

  async handlePDFURLChanged(url: string) {
    if (this.loadedPDFURL === url) return;
    this.loadedPDFURL = url;
    const info = await this.wb.pdfController.load(
      url,
      this.pageNumberAsNumber || 1
    );
    this.pdfInfo = info;
    this.pdfBackingCanvas = document.createElement("canvas");
    this.pdfBackingCanvas.width = this.pdfInfo.size.x;
    this.pdfBackingCanvas.height = this.pdfInfo.size.y;
    const pdfCtx = this.pdfBackingCanvas.getContext("2d");
    if (!pdfCtx) return; // some unlikely error
    await this.pdfInfo.render(pdfCtx, 1);
    this.redraw();
  }

  handleImageLoaded = () => {
    this.redraw();
  };

  get quillHelperShapeData(): QuillHelperShapeData {
    return {
      text: this.nodeShape.text,
      maxWidth:
        (this.nodeShape.pointB[0] -
          this.nodeShape.pointA[0] -
          this.textPadding.x * 2) /
        shapeScale2(this.shape).x,
    };
  }

  get nodeShape(): NodeShape {
    return this.shape as NodeShape;
  }

  get isFocused(): boolean {
    return this.quillHelper.fakeFocus;
  }

  save(scope = Scope.Persist) {
    this.wb.shapeStore.sendUpsertSingle(scope, this.id, {
      key: AttributeKey.Text,
      value: this.quillHelper.quill.getContents(),
    });
  }

  update(newShape: Shape2, isLive = true) {
    super.update(newShape, isLive);
    if (isLive) {
      this.handleImageURLChanged(this.nodeShape.imageURL);
    }
    this.quillHelper.setIsEnabled(this.nodeShape.imageURL === undefined);
    if (!this.nodeShape.imageURL) {
      this.quillHelper.update(this.quillHelperShapeData, isLive);
      this.applyTextElementTransform();
    }
  }

  applyTextElementTransform() {
    const origin = Vector.fromArray(this.nodeShape.pointA)
      .clone()
      .divide(shapeScale2(this.shape))
      .add(this.textPadding.clone().divide(shapeScale2(this.shape)));
    const newTransform = `translate3d(${origin.x}px, ${origin.y}px, 0)`;
    if (this.quillHelper.quillEl.style.translate !== newTransform)
      this.quillHelper.quillEl.style.transform = newTransform;
    this.quillHelper.applyMaxWidth();
  }

  drawShape(ctx: CanvasRenderingContext2D) {
    /* draw image if any */
    try {
      if (this.nodeShape.imageURL && this.image) {
        ctx.drawImage(
          this.image,
          this.nodeShape.pointA[0],
          this.nodeShape.pointA[1],
          this.nodeShape.pointB[0] - this.nodeShape.pointA[0],
          this.nodeShape.pointB[1] - this.nodeShape.pointA[1]
        );
      } else if (this.pdfBackingCanvas) {
        ctx.drawImage(
          this.pdfBackingCanvas,
          this.nodeShape.pointA[0],
          this.nodeShape.pointA[1],
          this.nodeShape.pointB[0] - this.nodeShape.pointA[0],
          this.nodeShape.pointB[1] - this.nodeShape.pointA[1]
        );
      }
      this.renderer.draw(ctx, this.shape);
    } catch (e) {
      console.error("Can't draw image: ", this.nodeShape.imageURL);
      console.log(
        "Check the Network tab of the browser dev tools and make sure the image actually loaded."
      );
    }
  }

  get selectionRect(): Rect {
    const shape = this.nodeShape;
    const origin = shapeTruePosition2(shape);
    const scale = new Vector(1, 1).divide(shapeScale2(shape));
    const a = origin
      .clone()
      .add(Vector.fromArray(shape.pointA).multiply(scale));
    const b = origin
      .clone()
      .add(Vector.fromArray(shape.pointB).multiply(scale));
    const inset = new Vector(0, 0);
    return new Rect(a, b).inset(inset);
  }

  renderShapeToCanvas(scaleFactor: number): HTMLCanvasElement {
    const canvas = super.renderShapeToCanvas(scaleFactor);
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;

    ctx.save();
    ctx.translate(this.nodeShape.pointA[0], this.nodeShape.pointA[1]);

    /* draw text */

    ctx.save();
    ctx.translate(this.textPadding.x, this.textPadding.y);
    this.quillHelper.renderShapeToCanvas(ctx, scaleFactor);
    ctx.restore();

    ctx.restore();

    return canvas;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applyEraser(shape: EraserShape2, id: string, redraw = true) {
    /* do nothing */
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeEraser(shape: EraserShape2, id: string, redraw = true) {
    /* do nothing */
  }
}
